/* Vitti PDP: one controller for carousels and ONE shared lightbox.
   No dependency on Shopify theme internals or third-party galleries. */
(() => {
  'use strict';

  const carouselInstances = new WeakMap();

  function setupCarousel(section) {
    if (carouselInstances.has(section)) return;
    const viewport = section.querySelector('[data-vitti-viewport]');
    const track = section.querySelector('[data-vitti-track]');
    const controls = section.querySelector('[data-vitti-controls]');
    const previous = section.querySelector('[data-vitti-prev]');
    const next = section.querySelector('[data-vitti-next]');
    if (!viewport || !track || !controls || !previous || !next) return;

    const update = () => {
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      controls.hidden = max <= 2;
      previous.disabled = viewport.scrollLeft <= 2;
      next.disabled = viewport.scrollLeft >= max - 2;
    };

    const move = direction => {
      const first = track.firstElementChild;
      if (!first) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      viewport.scrollBy({ left: direction * step, behavior: 'smooth' });
    };

    const onClick = event => {
      if (event.target.closest('[data-vitti-prev]')) move(-1);
      if (event.target.closest('[data-vitti-next]')) move(1);
    };

    const onScroll = () => update();
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    viewport.addEventListener('scroll', onScroll, { passive: true });
    section.addEventListener('click', onClick);
    requestAnimationFrame(update);

    carouselInstances.set(section, () => {
      observer.disconnect();
      viewport.removeEventListener('scroll', onScroll);
      section.removeEventListener('click', onClick);
    });
  }

  function setupAllCarousels(root = document) {
    if (root.matches?.('[data-vitti-carousel]')) setupCarousel(root);
    root.querySelectorAll('[data-vitti-carousel]').forEach(setupCarousel);
  }

  function cleanupCarousels(root) {
    const sections = [...root.querySelectorAll('[data-vitti-carousel]')];
    if (root.matches?.('[data-vitti-carousel]')) sections.push(root);
    sections.forEach(section => {
      carouselInstances.get(section)?.();
      carouselInstances.delete(section);
    });
  }

  class VittiLightbox {
    constructor(dialog) {
      this.dialog = dialog;
      this.stage = dialog.querySelector('[data-vitti-lightbox-stage]');
      this.image = dialog.querySelector('[data-vitti-lightbox-image]');
      this.review = dialog.querySelector('[data-vitti-lightbox-review]');
      this.stars = dialog.querySelector('[data-vitti-lightbox-stars]');
      this.quote = dialog.querySelector('[data-vitti-lightbox-quote]');
      this.author = dialog.querySelector('[data-vitti-lightbox-author]');
      this.counter = dialog.querySelector('[data-vitti-lightbox-counter]');
      this.items = [];
      this.index = 0;
      this.touchStart = null;
      this.ignoreClicksUntil = 0;
      this.returnFocus = null;
      this.previousOverflow = '';

      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-vitti-lightbox]');
        if (trigger && !this.dialog.contains(trigger)) {
          this.open(trigger);
          return;
        }
        if (!this.dialog.open) return;
        if (event.target.closest('[data-vitti-lightbox-close]')) this.dialog.close();
        else if (event.target.closest('[data-vitti-lightbox-prev]')) this.step(-1);
        else if (event.target.closest('[data-vitti-lightbox-next]')) this.step(1);
      });

      this.dialog.addEventListener('click', event => {
        if (Date.now() < this.ignoreClicksUntil) return;
        if (event.target === this.dialog) {
          this.dialog.close();
          return;
        }
        // Works for both images and review cards: click dark space, not content.
        const inStage = event.target.closest('[data-vitti-lightbox-stage]');
        const inContent = event.target.closest('[data-vitti-lightbox-image], [data-vitti-lightbox-review]');
        if (inStage && !inContent) this.dialog.close();
      });

      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow;
        this.image.removeAttribute('src');
        this.items = [];
        this.returnFocus?.focus({ preventScroll: true });
      });

      document.addEventListener('keydown', event => {
        if (!this.dialog.open) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
      });

      this.stage.addEventListener('touchstart', event => {
        const touch = event.changedTouches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      }, { passive: true });

      this.stage.addEventListener('touchend', event => {
        if (!this.touchStart) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touchStart.x;
        const dy = touch.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.ignoreClicksUntil = Date.now() + 350;
          this.step(dx < 0 ? 1 : -1);
        }
      }, { passive: true });
  }

  open(trigger) {
    const type = trigger.dataset.vittiLightbox;
    const group = trigger.dataset.vittiLightboxGroup;
    this.items = [...document.querySelectorAll('[data-vitti-lightbox]')]
      .filter(item => item.dataset.vittiLightbox === type && item.dataset.vittiLightboxGroup === group);
    this.index = this.items.indexOf(trigger);
    if (this.index < 0) return;
    this.returnFocus = trigger;
    this.dialog.setAttribute('aria-label', type === 'review' ? 'Vitti customer reviews' : 'Vitti photo gallery');
    this.previousOverflow = document.documentElement.style.overflow;
    this.display();
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  step(delta) {
    if (!this.items.length) return;
    this.index = (this.index + delta + this.items.length) % this.items.length;
    this.display();
  }

  display() {
    const item = this.items[this.index];
    const type = item.dataset.vittiLightbox;
    this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
    this.image.hidden = type !== 'image';
    this.review.hidden = type !== 'review';

    if (type === 'image') {
      this.image.width = Number(item.dataset.vittiLightboxWidth) || 1200;
      this.image.height = Number(item.dataset.vittiLightboxHeight) || 1200;
      this.image.alt = item.dataset.vittiLightboxAlt || '';
      if (this.image.getAttribute('src') !== item.dataset.vittiLightboxSrc) {
        this.image.src = item.dataset.vittiLightboxSrc;
      }
    } else {
      this.image.removeAttribute('src');
      const stars = item.querySelector('[data-vitti-review-stars]');
      const quote = item.querySelector('[data-vitti-review-quote]');
      const author = item.querySelector('[data-vitti-review-author]');
      this.stars.textContent = stars?.textContent || '';
      this.stars.style.setProperty('--rating', stars?.style.getPropertyValue('--rating') || '100%');
      this.stars.setAttribute('aria-label', stars?.getAttribute('aria-label') || '');
      this.quote.textContent = quote?.textContent.trim() || '';
      this.author.textContent = author?.textContent.trim() || '';
    }
  }
  }

  const init = () => {
    setupAllCarousels();
    const dialog = document.querySelector('[data-vitti-lightbox-dialog]');
    if (dialog) new VittiLightbox(dialog);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', event => setupAllCarousels(event.target));
  document.addEventListener('shopify:section:unload', event => cleanupCarousels(event.target));
})();